import { mapMaybe } from '@execonline-inc/collections';
import { when } from '@execonline-inc/maybe-adapter';
import { Emptyable, Maybe, just, nothing } from 'maybeasy';
import { includes } from 'ramda';
import { AttributeResource, LearningDesignResource, OfferingType } from '../../../../../../Types';
import { CompetencyFilter } from '../CompetencyFilter/Types';
import { ProductTypeFilter } from '../ProductTypeFilter/Types';

const hasNameText = (competency: AttributeResource): Maybe<string> =>
  competency.payload.name.map((t) => t.text);

const whenEmpty = <T extends Emptyable>(xs: T): Maybe<T> =>
  xs.length === 0 ? just<T>(xs) : nothing<T>();

const hasNoCompetencies = (
  learningDesignResource: LearningDesignResource,
): Maybe<LearningDesignResource> =>
  whenEmpty(learningDesignResource.payload.competencies).map(() => learningDesignResource);

const anyMatchingCompetency = (
  experiencePrimaryCompetencies: ReadonlyArray<string>,
  learningDesignResource: LearningDesignResource,
  competencies: ReadonlyArray<string>,
): Maybe<LearningDesignResource> =>
  when(
    experiencePrimaryCompetencies.some((competency) => includes(competency, competencies)),
    learningDesignResource,
  );

const experiencePrimaryCompetencies = (
  learningDesignResource: LearningDesignResource,
): ReadonlyArray<string> => mapMaybe(hasNameText, learningDesignResource.payload.competencies);

const experienceOfferingTypes = (
  learningDesignResource: LearningDesignResource,
): ReadonlyArray<OfferingType> => learningDesignResource.payload.offeringTypes;

export const whenAnyMatchingCompetencyOrHasNoCompetencies =
  (competencyFilter: CompetencyFilter) =>
  (learningDesignResource: LearningDesignResource): Maybe<LearningDesignResource> =>
    hasNoCompetencies(learningDesignResource).orElse(() =>
      anyMatchingCompetency(
        experiencePrimaryCompetencies(learningDesignResource),
        learningDesignResource,
        competencyFilter.competencies,
      ),
    );

const hasNoProductType = (
  learningDesignResource: LearningDesignResource,
): Maybe<LearningDesignResource> =>
  whenEmpty(learningDesignResource.payload.offeringTypes).map(() => learningDesignResource);

const anyMatchingProductType = (
  experienceOfferingTypes: ReadonlyArray<OfferingType>,
  learningDesignResource: LearningDesignResource,
  offeringTypes: ReadonlyArray<string>,
): Maybe<LearningDesignResource> =>
  when(
    experienceOfferingTypes.some((offeringType) => includes(offeringType, offeringTypes)),
    learningDesignResource,
  );

export const whenAnyMatchingProductTypeOrHasNoProductTypes =
  (productTypeFilter: ProductTypeFilter) =>
  (learningDesignResource: LearningDesignResource): Maybe<LearningDesignResource> =>
    hasNoProductType(learningDesignResource).orElse(() =>
      anyMatchingProductType(
        experienceOfferingTypes(learningDesignResource),
        learningDesignResource,
        productTypeFilter.productTypes,
      ),
    );

export const showFilteredLearningDesigns = (
  competencyFilter: CompetencyFilter,
  productTypeFilter: ProductTypeFilter,
  learningDesignResource: LearningDesignResource,
): Maybe<LearningDesignResource> =>
  just(learningDesignResource)
    .andThen(whenAnyMatchingCompetencyOrHasNoCompetencies(competencyFilter))
    .andThen(whenAnyMatchingProductTypeOrHasNoProductTypes(productTypeFilter));
